/* eslint-disable max-lines */
import {
  Box,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  Grid2,
  ListSubheader,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { Control, Controller, FieldError, FieldValues, Path } from 'react-hook-form';
import { Option, OptionValue } from '@customTypes';
import useCustomTranslation from '@hooks/useCustomTranslation';
import { EMPTY_STRING, FIRST_ELEMENT } from '@constants';
import { truncateString } from '@utils/helpers';
import { useReadOnly } from '@hooks/useReadOnly';
import TooltipIcon from '@components/TooltipIcon';

import styles from './Filter.module.scss';
import { StyledBriefcaseIcon } from './Filter.styled';

interface FilterProps<T extends FieldValues> {
  label?: string;
  options?: Option[] | Record<string, Option[]>;
  control: Control<T>;
  name: Path<T>;
  defaultValue?: string;
  isLoading?: boolean;
  placeholder?: string;
  size?: 'small' | 'medium';
  useLabelAsValue?: boolean;
  emptyItemLabel?: string;
  helperText?: string;
  errorText?: string;
  children?: React.ReactNode;
  isThisBusiness?: boolean;
  isDisabled?: boolean;
  maxOptionLength?: number;
  isInsidePopup?: boolean;
  tooltipText?: string;
}

const Filter = <T extends FieldValues>({
  label,
  options,
  control,
  name,
  defaultValue,
  isLoading,
  placeholder,
  size = 'small',
  useLabelAsValue,
  emptyItemLabel,
  helperText,
  errorText,
  children,
  isThisBusiness,
  isDisabled,
  maxOptionLength,
  isInsidePopup,
  tooltipText,
}: FilterProps<T>) => {
  const { getTranslation } = useCustomTranslation();
  const readOnly = useReadOnly();

  const renderOptions = () => {
    const defaultItems = emptyItemLabel
      ? [
          <MenuItem key="all" value={EMPTY_STRING}>
            {emptyItemLabel}
          </MenuItem>,
        ]
      : [];

    if (!options)
      return (
        <Typography pl={1.5} color="textSecondary">
          {getTranslation('Cvaproj.Setup75', 'noValues')}
        </Typography>
      );
    if (Array.isArray(options)) {
      return [
        ...defaultItems,
        ...options.map(({ value, label }, index) => (
          <MenuItem key={`${index}-${value}`} value={useLabelAsValue ? label : value}>
            {label}
          </MenuItem>
        )),
      ];
    } else {
      return Object.entries(options ?? {}).flatMap(([groupLabel, groupOptions], groupIndex) => [
        ...defaultItems,
        ...[
          groupIndex !== FIRST_ELEMENT && <Divider key={`divider-${groupIndex}`} />,
          <ListSubheader key={`header-${groupLabel}`}>{groupLabel}</ListSubheader>,
          ...groupOptions.map(({ value, label }, optionIndex) => (
            <MenuItem key={`${groupIndex}-${optionIndex}-${value}`} value={useLabelAsValue ? label : value}>
              {label}
            </MenuItem>
          )),
        ],
      ]);
    }
  };

  const getHelperText = (error?: FieldError) => {
    if (!error) {
      return helperText;
    }

    if (errorText) {
      return errorText;
    }

    if (error.message?.includes('CvaInput.') || error.message?.includes('CvaProj.')) {
      return getTranslation(error.message || EMPTY_STRING, `validationIntegration.${error.message}`);
    }

    return error.message;
  };

  return (
    <Grid2 container gap={0.3} className={styles.container}>
      {label && (
        <Box className={styles.filterHeader}>
          <div className={styles.titleContainer}>
            <Typography variant="header" color="textSecondary">
              {label}
            </Typography>
            {tooltipText && <TooltipIcon title={<span dangerouslySetInnerHTML={{ __html: tooltipText }} />} />}
          </div>
          {children && children}
        </Box>
      )}
      <FormControl size={size} fullWidth>
        <Controller
          name={name}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <>
              <Select
                variant="outlined"
                {...field}
                value={field.value ?? EMPTY_STRING}
                error={!!error}
                IconComponent={isLoading ? CircularProgress : undefined}
                disabled={isLoading || isDisabled}
                displayEmpty
                readOnly={readOnly}
                defaultValue={defaultValue}
                renderValue={(value: OptionValue) => {
                  if (value === undefined || value === null || value === EMPTY_STRING) {
                    if (!emptyItemLabel) {
                      return (
                        <Typography color="gray">
                          {placeholder ? placeholder : getTranslation('CvaInput.PricePane4', 'select')}
                        </Typography>
                      );
                    } else {
                      return <Typography>{emptyItemLabel}</Typography>;
                    }
                  }
                  const label = Array.isArray(options)
                    ? (options.find((el) => (el.value && useLabelAsValue ? el.label : el.value) === value)?.label ??
                      EMPTY_STRING)
                    : (Object.values(options ?? {})
                        .flat()
                        .find((el) => (el.value && useLabelAsValue ? el.label : el.value) === value)?.label ??
                      EMPTY_STRING);

                  const hasTooltip = maxOptionLength && label.length;

                  return (
                    <Box className={styles.iconWrapper}>
                      {isThisBusiness && <StyledBriefcaseIcon />}
                      <Tooltip title={hasTooltip ? label : EMPTY_STRING}>
                        <Typography>{hasTooltip ? truncateString(label, maxOptionLength) : label}</Typography>
                      </Tooltip>
                    </Box>
                  );
                }}
                MenuProps={{
                  disableScrollLock: true,
                  disablePortal: !isInsidePopup,
                  ...(isInsidePopup && {
                    MenuListProps: {
                      className: styles.list,
                    },
                  }),
                }}
                className={styles.select}
              >
                {renderOptions()}
              </Select>
              <FormHelperText hidden={!error && !helperText} error={!!error}>
                {getHelperText(error)}
              </FormHelperText>
            </>
          )}
        />
      </FormControl>
    </Grid2>
  );
};

export default Filter;
